import { useState } from 'react';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Lock, Bell, CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import type { ServiceArea } from '@/config/serviceAreas';

interface AreaCardProps {
  area: ServiceArea;
  onClick?: () => void;
}

export function AreaCard({ area, onClick }: AreaCardProps) {
  const { user } = useAuth();
  const Icon = area.icon;
  const isDisabled = !area.isActive;
  const [showNotifyForm, setShowNotifyForm] = useState(false);
  const [email, setEmail] = useState('');
  const [registering, setRegistering] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleNotifyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (user) {
      registerInterest();
    } else {
      setShowNotifyForm(true);
    }
  };

  const registerInterest = async (emailParam?: string) => {
    setRegistering(true);
    try {
      const { error } = await supabase.from('interes_areas').insert({
        area_slug: area.id,
        user_id: user?.id || null,
        email: emailParam || null,
      });
      
      if (error?.code === '23505') {
        toast.info('Ya estás registrado para esta área');
        setRegistered(true);
      } else if (error) {
        throw error;
      } else {
        toast.success(`Te avisaremos cuando ${area.name} esté disponible`);
        setRegistered(true);
      }
      setShowNotifyForm(false);
    } catch (err) {
      console.error(err);
      toast.error('Error al registrar interés');
    } finally {
      setRegistering(false);
    }
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (email.includes('@')) registerInterest(email);
  };

  return (
    <motion.div
      whileHover={!isDisabled ? { y: -6 } : undefined}
      whileTap={!isDisabled ? { scale: 0.97 } : undefined}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="h-full"
    >
      <div
        className={`
          group relative h-full rounded-2xl overflow-hidden
          border border-border/60 bg-card
          transition-all duration-300
          ${isDisabled 
            ? 'opacity-65 cursor-default' 
            : 'cursor-pointer hover:border-primary/40 hover:shadow-[0_8px_30px_-12px_hsl(var(--primary)/0.25)]'
          }
        `}
        onClick={isDisabled ? undefined : onClick}
      >
        {/* Gradient accent bar */}
        <div 
          className={`
            h-1 w-full transition-all duration-500
            ${isDisabled 
              ? 'bg-muted' 
              : `bg-gradient-to-r from-primary via-accent to-secondary`
            }
          `}
          style={{
            transform: isHovered && !isDisabled ? 'scaleX(1)' : 'scaleX(0.3)',
            transformOrigin: 'left',
            transition: 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        />

        {area.comingSoon && (
          <Badge 
            variant="secondary" 
            className="absolute top-4 right-4 text-[10px] uppercase tracking-wider font-semibold bg-muted/80 text-muted-foreground backdrop-blur-sm border-0"
          >
            Próximamente
          </Badge>
        )}
        
        <div className="p-5 sm:p-6 flex flex-col gap-4">
          {/* Icon with animated background */}
          <div className="relative">
            <motion.div
              className={`
                inline-flex items-center justify-center w-14 h-14 rounded-2xl
                ${area.bgColor} relative z-10
                transition-colors duration-300
                ${!isDisabled ? 'group-hover:bg-primary/15' : ''}
              `}
              animate={isHovered && !isDisabled ? { rotate: [0, -5, 5, 0] } : {}}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
            >
              <Icon className={`w-7 h-7 ${area.color} transition-transform duration-300 ${!isDisabled ? 'group-hover:scale-110' : ''}`} />
              {isDisabled && (
                <div className="absolute -bottom-1 -right-1 p-1 bg-muted rounded-full border-2 border-card">
                  <Lock className="w-3 h-3 text-muted-foreground" />
                </div>
              )}
            </motion.div>

            {/* Glow effect on hover */}
            {!isDisabled && (
              <div 
                className="absolute inset-0 w-14 h-14 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl -z-0"
                style={{ background: `hsl(var(--primary) / 0.15)` }}
              />
            )}
          </div>

          {/* Content */}
          <div className="space-y-1.5">
            <h3 className={`
              font-bold text-base tracking-tight text-foreground
              transition-colors duration-200
              ${!isDisabled ? 'group-hover:text-primary' : ''}
            `}>
              {area.name}
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
              {area.description}
            </p>
          </div>

          {/* Active area: explore CTA */}
          {!isDisabled && !area.comingSoon && (
            <div className="flex items-center gap-1.5 text-sm font-medium text-primary opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-0 group-hover:translate-x-1">
              <span>Explorar</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          )}

          {/* Notify me button for coming soon areas */}
          {area.comingSoon && !registered && !showNotifyForm && (
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-2 mt-auto rounded-xl border-dashed hover:border-primary/40 hover:bg-primary/5 transition-all duration-200"
              onClick={handleNotifyClick}
              disabled={registering}
            >
              {registering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
              <span className="text-xs">Avísame cuando esté disponible</span>
            </Button>
          )}

          {/* Email form for anonymous users */}
          {showNotifyForm && (
            <form onSubmit={handleEmailSubmit} className="flex gap-2 mt-auto" onClick={e => e.stopPropagation()}>
              <Input
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="h-9 text-xs rounded-xl"
                required
              />
              <Button type="submit" size="sm" className="h-9 px-3 rounded-xl" disabled={registering}>
                {registering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
              </Button>
            </form>
          )}

          {/* Registered confirmation */}
          {registered && (
            <motion.div 
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 text-xs text-success mt-auto"
            >
              <CheckCircle className="w-4 h-4" />
              <span className="font-medium">Te notificaremos</span>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
